import type { QuestionnaireScreen } from "./flow";
import type {
  AirlineReason,
  ArrivalDelay,
  Consequence,
  CoPassengerStatus,
  CorrectionItem,
  InformationChannel,
  InformationTiming,
  NotTravelledReason,
  OtherIssue,
  PriorStep,
  QuestionBlock,
  QuestionnaireLocale,
  ReroutingMode,
  VerificationStatus,
} from "./types";

type Option = { label: string; hint?: string };
type Options<Key extends string> = Record<Key, Option>;

/**
 * Everything the passenger reads, in both languages.
 *
 * Placeholders in `{braces}` are filled with `fillCopy`. Wording rules: no past participle that
 * would need a gender (« êtes-vous arrivé ») — the passenger is not always a man; no threshold or
 * amount next to a choice — it would nudge the answer.
 */
export type QuestionnaireCopy = {
  screens: Record<QuestionnaireScreen, { title: string; subtitle?: string }>;
  verification: Options<VerificationStatus>;
  corrections: Options<CorrectionItem>;
  notTravelled: Options<NotTravelledReason>;
  rerouting: Options<ReroutingMode>;
  arrival: Options<ArrivalDelay>;
  informationTiming: Options<InformationTiming>;
  informationChannel: Options<InformationChannel>;
  priorSteps: Options<PriorStep>;
  expenses: { yes: Option; no: Option };
  preExistingClaim: { no: Option; yes: Option };
  airlineReason: Options<AirlineReason>;
  consequences: Options<Consequence>;
  otherIssues: Options<OtherIssue>;
  coPassengerStatus: Options<CoPassengerStatus>;
  /** The same, short enough for a chip on a phone. */
  coPassengerStatusShort: Options<CoPassengerStatus>;
  incidentNouns: { cancellation: string; scheduleChange: string; default: string };
  flight: {
    reservation: string;
    scheduled: string;
    actual: string;
    departure: string;
    arrival: string;
    cancelled: string;
    delayedBy: string;
    detected: string;
    incident: Record<string, string>;
  };
  ui: Record<
    | "back"
    | "next"
    | "skip"
    | "answerRequired"
    | "edit"
    | "noAnswer"
    | "submitError"
    | "retry"
    | "flightNumber"
    | "flightNumberLabel"
    | "flightNumberShort"
    | "hideFlightNumber"
    | "precisionsPlaceholder"
    | "airlineReasonPlaceholder"
    | "teamQuestion"
    | "noNeedToWriteAirline"
    | "noBankDetails"
    | "bankDetailsWarning"
    | "extraordinaryTitle"
    | "extraordinaryText"
    | "extraordinaryExpenses"
    | "extraordinaryExpensesEmail"
    | "confirm"
    | "receiptsNextStep"
    | "receiptsStepName",
    string
  >;
};

const fr: QuestionnaireCopy = {
  screens: {
    VERIFICATION: {
      title: "Est-ce bien ce qui s'est passé ?",
      subtitle: "Voici ce que nous avons détecté sur votre réservation.",
    },
    CORRECTION: { title: "Qu'est-ce qui est différent ?", subtitle: "Plusieurs choix possibles." },
    NOT_TRAVELLED: {
      title: "Pourquoi n'avez-vous pas pris ce vol ?",
      subtitle: "Votre dossier ne sera pas envoyé à la compagnie : notre équipe le vérifiera d'abord.",
    },
    PRE_EXISTING_CLAIM: {
      title: "Avez-vous déjà réclamé une indemnisation ?",
      subtitle:
        "Nous avons besoin de savoir si une action a déjà été intentée auprès de la compagnie aérienne.",
    },
    REROUTING: { title: "La compagnie vous a-t-elle proposé un autre vol ?" },
    REROUTING_MEANS: { title: "Comment avez-vous rejoint votre destination ?" },
    ARRIVAL: {
      title: "À quelle heure avez-vous atteint votre destination finale ?",
      subtitle: "Par rapport à l'heure d'arrivée prévue, en heure locale.",
    },
    INFORMATION_TIMING: { title: "Quand avez-vous appris {incident} ?" },
    INFORMATION_CHANNEL: { title: "Comment l'avez-vous appris ?" },
    PRIOR_STEPS: {
      title: "Avez-vous déjà fait une démarche auprès de {airline} ?",
      subtitle: "Plusieurs choix possibles.",
    },
    PRIOR_STEPS_DETAILS: {
      title: "Quelques précisions sur ces démarches",
      subtitle: "Facultatif, mais cela nous fait gagner du temps.",
    },
    CO_PASSENGERS: {
      title: "Connaissez-vous l'adresse e-mail de ces passagers ?",
      subtitle: "Nous n'avons pas pu les joindre. Avec leur adresse, ils pourront être indemnisés eux aussi.",
    },
    EXPENSES: {
      title: "Avez-vous eu des frais à cause de cet incident ?",
      subtitle: "Repas, hôtel, transport, nouveau billet…",
    },
    AIRLINE_REASON: { title: "Quelle raison la compagnie vous a-t-elle donnée ?" },
    CONSEQUENCES: {
      title: "Qu'est-ce que cet incident a changé pour vous ?",
      subtitle: "Facultatif, plusieurs choix possibles.",
    },
    OTHER_ISSUES: {
      title: "Avez-vous eu d'autres problèmes pendant ce voyage ?",
      subtitle: "Facultatif. Ils relèvent d'autres règles : nous les traiterons à part.",
    },
    PRECISIONS: { title: "Souhaitez-vous ajouter quelque chose ?", subtitle: "Facultatif." },
    RECAP: { title: "Vérifiez vos réponses", subtitle: "Touchez une réponse pour la modifier." },
  },
  verification: {
    CONFIRMED: { label: "Oui, c'est bien ça" },
    CORRECTED: {
      label: "En partie, je corrige",
      hint: "Le vol, les horaires ou l'incident ne sont pas exacts",
    },
    NOT_TRAVELLED: { label: "Je n'ai pas pris ce vol" },
  },
  corrections: {
    FLIGHT: { label: "Le vol", hint: "Numéro, date ou trajet" },
    TIMES: { label: "Les horaires" },
    INCIDENT_TYPE: { label: "Le type d'incident", hint: "Par exemple un retard plutôt qu'une annulation" },
    NO_INCIDENT: { label: "Le vol s'est déroulé normalement" },
  },
  notTravelled: {
    OTHER_FLIGHT: { label: "J'avais changé de vol avant l'incident" },
    PLANS_CHANGED: { label: "J'ai annulé ou reporté mon voyage" },
    HEALTH_OR_FAMILY: { label: "Raison de santé ou familiale" },
    OTHER: { label: "Autre raison" },
  },
  rerouting: {
    AIRLINE: { label: "Oui" },
    SELF_BOOKED: { label: "J'ai racheté un billet moi-même" },
    OTHER_TRANSPORT: { label: "Par un autre moyen de transport", hint: "Train, voiture, bus…" },
    ABANDONED: { label: "J'ai renoncé à ce voyage" },
    NOT_REROUTED: { label: "Non" },
  },
  arrival: {
    AROUND_SCHEDULED: { label: "Vers l'heure prévue" },
    BETWEEN_2_AND_3_HOURS: { label: "Entre 2 h et 3 h plus tard" },
    BETWEEN_3_AND_4_HOURS: { label: "Entre 3 h et 4 h plus tard" },
    MORE_THAN_4_HOURS: { label: "Plus de 4 h plus tard" },
    NEXT_DAY_OR_LATER: { label: "Le lendemain ou plus tard" },
    BETWEEN_1_AND_2_HOURS: { label: "1 à 2 h plus tard" },
    THREE_HOURS_OR_MORE: { label: "3 h plus tard ou plus" },
  },
  informationTiming: {
    MORE_THAN_14_DAYS: { label: "Plus de 14 jours avant le départ" },
    BETWEEN_7_AND_14_DAYS: { label: "Entre 7 et 14 jours avant le départ" },
    LESS_THAN_7_DAYS: { label: "Moins de 7 jours avant le départ" },
    AT_AIRPORT: { label: "Le jour même, à l'aéroport" },
    NEVER: { label: "Je n'ai reçu aucun message" },
  },
  informationChannel: {
    EMAIL: { label: "Par e-mail" },
    SMS: { label: "Par SMS" },
    APP: { label: "Dans l'application de la compagnie" },
    TRAVEL_AGENCY: { label: "Par mon agence de voyage" },
    AIRPORT: { label: "À l'aéroport" },
  },
  priorSteps: {
    NONE: { label: "Aucune démarche" },
    CLAIM_PENDING: { label: "J'ai une réclamation en cours" },
    COMPENSATED: { label: "J'ai déjà reçu une indemnisation" },
    VOUCHER_ACCEPTED: { label: "J'ai accepté un bon d'achat ou un avoir" },
    TICKET_REFUNDED: { label: "Mon billet m'a été remboursé" },
  },
  expenses: {
    yes: { label: "Oui", hint: "Vous joindrez vos justificatifs à l'étape suivante" },
    no: { label: "Non" },
  },
  preExistingClaim: {
    no: { label: "Aucune réclamation n'a été faite" },
    yes: { label: "J'ai déjà effectué une demande auprès de la compagnie" },
  },
  airlineReason: {
    TECHNICAL: { label: "Un problème technique" },
    WEATHER: { label: "La météo" },
    STRIKE: { label: "Une grève" },
    NONE_GIVEN: { label: "Aucune raison" },
    OTHER: { label: "Autre" },
  },
  consequences: {
    MISSED_CONNECTION: { label: "Une correspondance manquée" },
    UNPLANNED_NIGHT: { label: "Une nuit imprévue sur place" },
    WORK_OR_APPOINTMENT: { label: "Un jour de travail ou un rendez-vous perdu" },
    MISSED_EVENT: { label: "Un événement manqué" },
    LOST_HOLIDAY: { label: "Un jour de vacances perdu" },
    VULNERABLE_PERSON: { label: "Je voyageais avec un enfant ou une personne vulnérable" },
    STRESS_FATIGUE: { label: "Du stress ou de la fatigue" },
  },
  otherIssues: {
    BAGGAGE: { label: "Bagage retardé, perdu ou abîmé" },
    DOWNGRADE: { label: "Déclassement", hint: "Une classe inférieure à celle réservée" },
    PAID_SERVICE_NOT_PROVIDED: { label: "Siège ou option payés non fournis" },
  },
  coPassengerStatus: {
    TRAVELLED: { label: "A voyagé" },
    EMAIL: { label: "Voici son adresse e-mail" },
    SAME_AS_ME: { label: "Même adresse e-mail que moi" },
    MINOR_REPRESENTED: { label: "Mineur, je suis son représentant légal" },
    DID_NOT_TRAVEL: { label: "N'a pas voyagé" },
    UNKNOWN: { label: "Je ne sais pas" },
  },
  coPassengerStatusShort: {
    TRAVELLED: { label: "A voyagé" },
    EMAIL: { label: "Son e-mail" },
    SAME_AS_ME: { label: "Même e-mail que moi" },
    MINOR_REPRESENTED: { label: "Mineur, je le représente" },
    DID_NOT_TRAVEL: { label: "N'a pas voyagé" },
    UNKNOWN: { label: "Je ne sais pas" },
  },
  incidentNouns: {
    cancellation: "l'annulation de votre vol",
    scheduleChange: "le changement d'horaire",
    default: "cet incident",
  },
  flight: {
    reservation: "Réservation {reference}",
    scheduled: "Prévu",
    actual: "Réel",
    departure: "Départ",
    arrival: "Arrivée",
    cancelled: "Vol annulé",
    delayedBy: "Arrivé avec {duration} de retard",
    detected: "Incident détecté",
    incident: {
      FLIGHT_DELAY: "Retard",
      DELAY_FROM_PNR_SCHEDULE: "Changement d'horaire",
      FLIGHT_CANCELLATION: "Annulation",
      PNR_SEGMENT_CANCELLATION: "Annulation",
      OVERBOOKING: "Refus d'embarquement",
      MISSED_CONNECTION_DUE_TO_DELAY: "Correspondance manquée",
      CONNECTION_DISRUPTION: "Correspondance manquée",
      WRONG_ARRIVAL_AIRPORT: "Déroutement",
      COVID_ORDINANCE_2020: "Annulation",
    },
  },
  ui: {
    back: "Retour",
    next: "Continuer",
    skip: "Passer",
    answerRequired: "Choisissez une réponse pour continuer.",
    edit: "Modifier",
    noAnswer: "Pas de réponse",
    submitError: "Impossible d'envoyer vos réponses. Vérifiez votre connexion, puis réessayez.",
    retry: "Réessayer",
    flightNumber: "Saisir le numéro de vol",
    flightNumberLabel: "Numéro du vol proposé par la compagnie",
    flightNumberShort: "vol",
    hideFlightNumber: "Ne pas préciser",
    precisionsPlaceholder: "Votre réponse",
    airlineReasonPlaceholder: "Laquelle ? (ex. : équipage manquant)",
    teamQuestion: "Question de notre équipe",
    noNeedToWriteAirline: "Pas besoin d'écrire à la compagnie : nous nous en chargeons.",
    noBankDetails: "N'indiquez ni IBAN ni coordonnées bancaires.",
    bankDetailsWarning:
      "Il semble que vous ayez indiqué des coordonnées bancaires. Retirez-les : nous vous les demanderons de façon sécurisée si besoin.",
    extraordinaryTitle: "Circonstances extraordinaires probables",
    // Followed by `extraordinaryExpenses`, shown in bold: « …En revanche, vos **frais supplémentaires…** ».
    extraordinaryText:
      "Il est probable que votre vol ait été perturbé en raison de circonstances extraordinaires, ce qui exonérerait la compagnie du versement de l'indemnité. En revanche, vos",
    // On the page, followed by the « Ajouter vos frais » step pill; the email reader has nothing to add yet.
    extraordinaryExpenses:
      "frais supplémentaires (nuit d'hôtel, repas…) pourraient vous être remboursés : ajoutez vos justificatifs à l'étape suivante",
    extraordinaryExpensesEmail:
      "frais supplémentaires (nuit d'hôtel, repas…) pourraient vous être remboursés : gardez vos justificatifs.",
    confirm: "Confirmer l'incident",
    receiptsNextStep: "Vous pourrez joindre vos factures (billet, train, hôtel…) à l'étape suivante",
    // As the page's stepper numbers and names it.
    receiptsStepName: "2. Ajouter vos frais",
  },
};

const en: QuestionnaireCopy = {
  screens: {
    VERIFICATION: {
      title: "Is this what happened?",
      subtitle: "Here is what we detected on your booking.",
    },
    CORRECTION: { title: "What was different?", subtitle: "Select all that apply." },
    NOT_TRAVELLED: {
      title: "Why didn't you take this flight?",
      subtitle: "Your file will not be sent to the airline: our team will review it first.",
    },
    PRE_EXISTING_CLAIM: {
      title: "Have you already claimed compensation?",
      subtitle: "We need to know whether any action has already been taken with the airline.",
    },
    REROUTING: { title: "Did the airline put you on another flight?" },
    REROUTING_MEANS: { title: "How did you reach your destination?" },
    ARRIVAL: {
      title: "When did you reach your final destination?",
      subtitle: "Compared with the scheduled arrival time, in local time.",
    },
    INFORMATION_TIMING: { title: "When did you learn about {incident}?" },
    INFORMATION_CHANNEL: { title: "How did you find out?" },
    PRIOR_STEPS: {
      title: "Have you already contacted {airline} about this?",
      subtitle: "Select all that apply.",
    },
    PRIOR_STEPS_DETAILS: {
      title: "A few details about these steps",
      subtitle: "Optional, but it saves us time.",
    },
    CO_PASSENGERS: {
      title: "Do you know these passengers' email addresses?",
      subtitle: "We could not reach them. With their address, they can be compensated too.",
    },
    EXPENSES: {
      title: "Did this incident cost you anything?",
      subtitle: "Meals, hotel, transport, a new ticket…",
    },
    AIRLINE_REASON: { title: "What reason did the airline give you?" },
    CONSEQUENCES: {
      title: "How did this incident affect you?",
      subtitle: "Optional, select all that apply.",
    },
    OTHER_ISSUES: {
      title: "Did anything else go wrong on this trip?",
      subtitle: "Optional. Different rules apply: we will handle these separately.",
    },
    PRECISIONS: { title: "Anything you would like to add?", subtitle: "Optional." },
    RECAP: { title: "Check your answers", subtitle: "Tap an answer to change it." },
  },
  verification: {
    CONFIRMED: { label: "Yes, that's right" },
    CORRECTED: { label: "Partly, let me correct it", hint: "The flight, times or incident are not right" },
    NOT_TRAVELLED: { label: "I didn't take this flight" },
  },
  corrections: {
    FLIGHT: { label: "The flight", hint: "Number, date or route" },
    TIMES: { label: "The times" },
    INCIDENT_TYPE: { label: "The type of incident", hint: "For example a delay rather than a cancellation" },
    NO_INCIDENT: { label: "The flight went normally" },
  },
  notTravelled: {
    OTHER_FLIGHT: { label: "I had changed flights before the incident" },
    PLANS_CHANGED: { label: "I cancelled or postponed my trip" },
    HEALTH_OR_FAMILY: { label: "Health or family reasons" },
    OTHER: { label: "Another reason" },
  },
  rerouting: {
    AIRLINE: { label: "Yes" },
    SELF_BOOKED: { label: "I bought a new ticket myself" },
    OTHER_TRANSPORT: { label: "Another way", hint: "Train, car, bus…" },
    ABANDONED: { label: "I gave up on the trip" },
    NOT_REROUTED: { label: "No" },
  },
  arrival: {
    AROUND_SCHEDULED: { label: "Around the scheduled time" },
    BETWEEN_2_AND_3_HOURS: { label: "2 to 3 hours later" },
    BETWEEN_3_AND_4_HOURS: { label: "3 to 4 hours later" },
    MORE_THAN_4_HOURS: { label: "More than 4 hours later" },
    NEXT_DAY_OR_LATER: { label: "The next day or later" },
    BETWEEN_1_AND_2_HOURS: { label: "1 to 2 hours later" },
    THREE_HOURS_OR_MORE: { label: "3 hours later or more" },
  },
  informationTiming: {
    MORE_THAN_14_DAYS: { label: "More than 14 days before departure" },
    BETWEEN_7_AND_14_DAYS: { label: "7 to 14 days before departure" },
    LESS_THAN_7_DAYS: { label: "Less than 7 days before departure" },
    AT_AIRPORT: { label: "On the day, at the airport" },
    NEVER: { label: "I never received any message" },
  },
  informationChannel: {
    EMAIL: { label: "By email" },
    SMS: { label: "By text message" },
    APP: { label: "In the airline's app" },
    TRAVEL_AGENCY: { label: "Through my travel agency" },
    AIRPORT: { label: "At the airport" },
  },
  priorSteps: {
    NONE: { label: "Nothing yet" },
    CLAIM_PENDING: { label: "I have a claim in progress" },
    COMPENSATED: { label: "I have already been compensated" },
    VOUCHER_ACCEPTED: { label: "I accepted a voucher or credit" },
    TICKET_REFUNDED: { label: "My ticket was refunded" },
  },
  expenses: {
    yes: { label: "Yes", hint: "You will upload your receipts in the next step" },
    no: { label: "No" },
  },
  preExistingClaim: {
    no: { label: "No claim has been made" },
    yes: { label: "I have already made a request to the airline" },
  },
  airlineReason: {
    TECHNICAL: { label: "A technical problem" },
    WEATHER: { label: "The weather" },
    STRIKE: { label: "A strike" },
    NONE_GIVEN: { label: "No reason" },
    OTHER: { label: "Other" },
  },
  consequences: {
    MISSED_CONNECTION: { label: "A missed connection" },
    UNPLANNED_NIGHT: { label: "An unplanned night away" },
    WORK_OR_APPOINTMENT: { label: "A lost work day or appointment" },
    MISSED_EVENT: { label: "A missed event" },
    LOST_HOLIDAY: { label: "A lost holiday day" },
    VULNERABLE_PERSON: { label: "I was travelling with a child or a vulnerable person" },
    STRESS_FATIGUE: { label: "Stress or tiredness" },
  },
  otherIssues: {
    BAGGAGE: { label: "Delayed, lost or damaged baggage" },
    DOWNGRADE: { label: "Downgrade", hint: "A lower class than the one booked" },
    PAID_SERVICE_NOT_PROVIDED: { label: "Paid seat or extra not provided" },
  },
  coPassengerStatus: {
    TRAVELLED: { label: "Travelled" },
    EMAIL: { label: "Here is their email address" },
    SAME_AS_ME: { label: "Same email address as mine" },
    MINOR_REPRESENTED: { label: "Minor, I am their legal guardian" },
    DID_NOT_TRAVEL: { label: "Did not travel" },
    UNKNOWN: { label: "I don't know" },
  },
  coPassengerStatusShort: {
    TRAVELLED: { label: "Travelled" },
    EMAIL: { label: "Their email" },
    SAME_AS_ME: { label: "Same email as mine" },
    MINOR_REPRESENTED: { label: "Minor, I'm their guardian" },
    DID_NOT_TRAVEL: { label: "Didn't travel" },
    UNKNOWN: { label: "I don't know" },
  },
  incidentNouns: {
    cancellation: "the cancellation",
    scheduleChange: "the schedule change",
    default: "this incident",
  },
  flight: {
    reservation: "Booking {reference}",
    scheduled: "Scheduled",
    actual: "Actual",
    departure: "Departure",
    arrival: "Arrival",
    cancelled: "Flight cancelled",
    delayedBy: "Arrived {duration} late",
    detected: "Detected incident",
    incident: {
      FLIGHT_DELAY: "Delay",
      DELAY_FROM_PNR_SCHEDULE: "Schedule change",
      FLIGHT_CANCELLATION: "Cancellation",
      PNR_SEGMENT_CANCELLATION: "Cancellation",
      OVERBOOKING: "Denied boarding",
      MISSED_CONNECTION_DUE_TO_DELAY: "Missed connection",
      CONNECTION_DISRUPTION: "Missed connection",
      WRONG_ARRIVAL_AIRPORT: "Diversion",
      COVID_ORDINANCE_2020: "Cancellation",
    },
  },
  ui: {
    back: "Back",
    next: "Continue",
    skip: "Skip",
    answerRequired: "Choose an answer to continue.",
    edit: "Edit",
    noAnswer: "No answer",
    submitError: "We couldn't send your answers. Check your connection, then try again.",
    retry: "Try again",
    flightNumber: "Enter the flight number",
    flightNumberLabel: "Number of the flight the airline offered",
    flightNumberShort: "flight",
    hideFlightNumber: "Don't specify",
    precisionsPlaceholder: "Your answer",
    airlineReasonPlaceholder: "Which one? (e.g. missing crew)",
    teamQuestion: "A question from our team",
    noNeedToWriteAirline: "No need to write to the airline: we take care of it.",
    noBankDetails: "Please don't include your IBAN or bank details.",
    bankDetailsWarning:
      "It looks like you entered bank details. Please remove them: we will ask for them securely if needed.",
    extraordinaryTitle: "Likely extraordinary circumstances",
    extraordinaryText:
      "Your flight was most likely disrupted by extraordinary circumstances, which would exempt the airline from paying compensation. However, your",
    extraordinaryExpenses:
      "additional costs (extra hotel night, meals…) may be refunded: add your receipts in the next step",
    extraordinaryExpensesEmail:
      "additional costs (extra hotel night, meals…) may be refunded: keep your receipts.",
    confirm: "Confirm the incident",
    receiptsNextStep: "You can add your receipts (ticket, train, hotel…) at the next step",
    receiptsStepName: "2. Add your expenses",
  },
};

export const QUESTIONNAIRE_COPY: Record<QuestionnaireLocale, QuestionnaireCopy> = { fr, en };

/** « Question {current} sur {total} » → « Question 2 sur 5 ». Unknown placeholders are left as-is. */
export function fillCopy(
  template: string,
  values: Record<string, string | number | null | undefined>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = values[key];

    return value === undefined || value === null ? match : String(value);
  });
}

/** The admin's names for the question blocks (dashboard, French only). */
export const QUESTION_BLOCK_LABELS: Record<QuestionBlock, { label: string; description: string }> = {
  REROUTING: {
    label: "Réacheminement et heure d'arrivée",
    description: "Autre vol proposé par la compagnie (oui / non), puis l'heure d'arrivée (par tranches).",
  },
  AIRLINE_REASON: {
    label: "Raison donnée par la compagnie",
    description: "Technique, météo, grève, aucune, autre.",
  },
  EXTRAORDINARY_CIRCUMSTANCES: {
    label: "Bannière « circonstances extraordinaires »",
    description:
      "Remplace « la compagnie pourrait vous devoir … € » sous le vol : pas d'indemnité probable, mais les frais supplémentaires peuvent être remboursés.",
  },
};
